"use client"

import Image from "next/image"
import Link from "next/link"
import React from "react"

import { Element } from "@/components/scroll/ScrollComponents"
import { CTAType } from "@/types/ctaCard"
import { VideoItem } from "@/types/video"
import ValueProps from "@/ui/patterns/ValueProps"

import LinkOutIcon from "../../../../public/swell/external-link.svg"
import CTACard from "../CTACard"
import PageGrid from "../PageGrid"
import PardotForm from "../PardotForm"
import SelectBlogPostsCarousel, { CarouselProps } from "../SelectPostsCarousel"
import UseCaseValuePropsSection from "../use-case/UseCaseValuePropsSection"
import VideoCarouselProvider from "../VideoCarouselProvider"
import VideoPlayer from "../VideoPlayer"

import HeroSection from "./SolutionsHero"
import SubHeroSection from "./SolutionsSubHero"
import StablecoinLayout from "./StablecoinLayout"
import ValuePropsSection from "./ValuePropsSection"

const nowAvailable = [
  {
    image: "/customer-logos/alchemy.png",
    link: "https://alchemypay.org/",
    height: 40,
    width: 209,
    alt: "alchemy Logo",
  },
  {
    image: "/customer-logos/archax-color-2.png",
    link: "https://archax.com/",
    height: 78,
    width: 99,
    alt: "archax Logo",
  },
  {
    image: "/customer-logos/b2c2-color.png",
    link: "https://www.b2c2.com/",
    height: 36,
    width: 159,
    alt: "b2c2 Logo",
  },
  {
    image: "/customer-logos/banxa-color.png",
    link: "https://www.banxa.com/",
    height: 36,
    width: 159,
    alt: "banxa Logo",
  },
  {
    image: "/customer-logos/bitget-color.png",
    link: "https://www.bitget.com/",
    height: 36,
    width: 159,
    alt: "bitget Logo",
  },
  {
    image: "/customer-logos/bitmex-color.png",
    link: "https://www.bitmex.com/app/trade/RLUSD_USDT",
    height: 58,
    width: 180,
    alt: "bitmex Logo",
  },
  {
    image: "/customer-logos/bitso-color.png",
    link: "https://bitso.com/",
    height: 48,
    width: 170,
    alt: "bitso Logo",
  },
  {
    image: "/customer-logos/bitstamp-color.png",
    link: "https://www.bitstamp.net/",
    height: 44,
    width: 200,
    alt: "bitstamp Logo",
  },
  {
    image: "/customer-logos/bullish-black.png",
    link: "https://bullish.com/",
    height: 34,
    width: 200,
    alt: "bullish Logo",
  },
  {
    image: "/customer-logos/coinmena-black-2.png",
    link: "https://www.coinmena.com/",
    height: 38,
    width: 326,
    alt: "coinmena Logo",
  },
  {
    image: "/customer-logos/flowdesk-color.png",
    link: "https://www.flowdesk.co/",
    height: 28,
    width: 159,
    alt: "flowdesk Logo",
  },
  {
    image: "/customer-logos/gemini-color.png",
    link: "https://www.gemini.com/",
    height: 40,
    width: 200,
    alt: "gemini Logo",
  },
  {
    image: "/customer-logos/independent-reserve-color.png",
    link: "https://www.independentreserve.com/",
    height: 40,
    width: 200,
    alt: "independent reserve Logo",
  },
  {
    image: "/customer-logos/jst-black.png",
    link: "https://jstdigital.io/",
    height: 39,
    width: 150,
    alt: "jst Logo",
  },
  {
    image: "/customer-logos/keyrock-black.png",
    link: "https://keyrock.com/",
    height: 36,
    width: 159,
    alt: "keyrock Logo",
  },
  {
    image: "/customer-logos/kraken-purple.png",
    link: "https://www.kraken.com/",
    height: 24,
    width: 159,
    alt: "kraken Logo",
  },
  {
    image: "/customer-logos/mercado-btc-color.png",
    link: "https://www.mercadobitcoin.com.br/?lang=en",
    height: 73,
    width: 67,
    alt: "mercado bitcoin Logo",
  },
  {
    image: "/customer-logos/lmax-color.jpg",
    link: "https://www.lmaxdigital.com/overview/",
    height: 35,
    width: 199,
    alt: "lmax Logo",
  },
  {
    image: "/customer-logos/moonpay-color.png",
    link: "http://moonpay.com/buy/rlusd",
    height: 33,
    width: 181,
    alt: "Moonpay Logo",
  },
  {
    image: "/customer-logos/pdax-color.png",
    link: "https://www.pdax.com/",
    height: 31,
    width: 162,
    alt: "Pdax Logo",
  },
  {
    image: "/customer-logos/revolut-black.png",
    link: "https://www.revolut.com/",
    height: 27,
    width: 159,
    alt: "revolut Logo",
  },
  {
    image: "/customer-logos/transak-color.png",
    link: "https://transak.com/",
    height: 57,
    width: 178,
    alt: "transak Logo",
  },
  {
    image: "/customer-logos/uphold-color.png",
    link: "https://uphold.com/en-us",
    height: 57,
    width: 178,
    alt: "uphold Logo",
  },
  {
    image: "/customer-logos/zerohash-color.png",
    link: "https://zerohash.com/",
    height: 29,
    width: 178,
    alt: "zerohash Logo",
  },
]

const rusdProps = {
  heading: "Ripple USD for Business: Intuition trusted by institutions",
  wrapperClass: "bg-gray-2.5",
  buttonLink: "#stablecoin-form",
  buttonCTA: "Get RLUSD for Business",
  mainButton: true,
  align: "center",
  valueProps: [
    {
      title: "Payment Service Providers",
      description:
        "Tap into new markets with a widely available, compliance- and payments-focused stablecoin.",
      image: (
        <Image
          width={128}
          height={128}
          sizes="(min-width: 128px) 128px, 100vw"
          src="/icons/payment.png"
          alt="Icon - Payment Service Providers"
          className="w-16"
        />
      ),
    },
    {
      title: "Remittance & Money Transfers",
      description:
        "Enable seamless and near-instant cross-border settlement with easy stablecoin, fiat and crypto asset on/off ramping.",
      image: (
        <Image
          width={128}
          height={128}
          sizes="(min-width: 128px) 128px, 100vw"
          src="/icons/capital.png"
          alt="Icon - Remittance & Money Transfer Providers"
          className="w-16"
        />
      ),
    },
    {
      title: "Centralized Exchanges",
      description:
        "Drive trading volume growth with a trusted, transparent stablecoin partner.",
      image: (
        <Image
          width={128}
          height={128}
          sizes="(min-width: 128px) 128px, 100vw"
          src="/icons/access-inclusion.png"
          alt="Icon - Centralized Exchanges"
          className="w-16"
        />
      ),
    },
  ],
}

const rusdProps2 = {
  heading: "Real-time, secure, compliant transactions",
  wrapperClass: "bg-gray-2.5",
  buttonLink: "#stablecoin-form",
  buttonCTA: "Talk To Our Team",
  description:
    "Ripple USD enables businesses to send funds worldwide, backed by stability, trust and compliance.",
  mainButton: true,
  align: "center",
  valueProps: [
    {
      title: "Near Instantaneous Payments",
      description:
        "Power peer-to-peer (P2P) or business payouts through RLUSD, with transactions that settle in seconds on XRP Ledger and Ethereum blockchains.",
      image: (
        <Image
          width={128}
          height={128}
          sizes="(min-width: 128px) 128px, 100vw"
          src="/icons/fast.png"
          alt="Icon - Near Instantaneous Payments"
          className="w-16"
        />
      ),
    },
    {
      title: "Global Stablecoin Settlement",
      description:
        "Integrate RLUSD as a foundational trading pair with other crypto assets, as an on-ramp for bringing fiat onchain, and as a settlement asset for derivatives markets.",
      image: (
        <Image
          width={128}
          height={128}
          sizes="(min-width: 128px) 128px, 100vw"
          src="/icons/new-markets.png"
          alt="Icon - Global Stablecoin Settlement"
          className="w-16"
        />
      ),
    },
    {
      title: "Compliant Crypto Transactions",
      description:
        "Enable merchant and vendor 3rd party payouts, fiat-to-RLUSD on/off ramps, and global settlement for FX markets.",
      image: (
        <Image
          width={128}
          height={128}
          sizes="(min-width: 128px) 128px, 100vw"
          src="/icons/service-layers.png"
          alt="Icon - Compliant Crypto Transactions"
          className="w-16"
        />
      ),
    },
  ],
}

const valueProps = {
  rowOne: [
    <ValueProps
      title="Deep Relationships, Deeper Stability"
      description="Ripple USD can be used for major trading pairs on select centralized exchanges."
      key="valueProp1"
    />,
    <ValueProps
      title="Multi-Chain Issuance"
      description="Issued on XRP Ledger and Ethereum, Ripple USD opens the door to multiple ecosystems."
      key="valueProp2"
    />,
  ],
  rowTwo: [
    <ValueProps
      title="Near Real-Time Settlement"
      description="Send Ripple USD worldwide in near real-time with minimal fees and no bank account required."
      key="valueProp3"
    />,
    <ValueProps
      title="Programmable Finance"
      description="Financial use cases are made more efficient and accessible with easy buying, on-ramping, trading and more."
      key="valueProp4"
    />,
  ],
}

interface stblPage {
  allVideos: VideoItem[]
  cardData: CTAType
  videoData: VideoItem
  carouselData: CarouselProps[]
}

const StablecoinPage: React.FC<stblPage> = ({
  allVideos,
  cardData,
  videoData,
  carouselData,
}) => (
  <StablecoinLayout>
    <div className="overflow-hidden">
      <HeroSection
        headline="Ripple USD (RLUSD) Stablecoin"
        subHeadline="Stable, transparent, trusted. Ripple USD."
        buttonCTA="Get RLUSD for Business"
        buttonLink="#stablecoin-form"
        secondaryLinkText="Download the Whitepaper"
        secondaryLink="/reports/rlusd-whitepaper.pdf"
      />
      <SubHeroSection
        image={<VideoPlayer {...videoData} />}
        title="Real-time payments and easy fiat to stablecoin on/off ramps."
        descriptions={[
          "Hold, buy, and trade tokenized USD onchain with the stablecoin that’s held to higher standards—and no reservations about our reserves.",
          "Ripple USD (RLUSD) is designed to maintain a constant value of one US dollar. Natively issued on XRP Ledger and Ethereum blockchains, Ripple USD is fully backed by a segregated reserve of cash and cash equivalents and redeemable 1:1 for US dollars.",
          "*Availability of RLUSD is dependent on jurisdiction.",
        ]}
      />

      <UseCaseValuePropsSection {...rusdProps} />

      <VideoCarouselProvider
        videos={allVideos}
        sectionTitle="RLUSD Stablecoin Videos"
        darkmode={false}
        className="bg-gray-0 text-gray-90"
      />

      <UseCaseValuePropsSection {...rusdProps2} />

      {/* logos section */}
      <section className="py-28 bg-gray-0">
        <PageGrid>
          <div className="col-span-full flex justify-center flex-col  text-center items-center">
            <h4 className="blue-gradient-bds headline4 max-w-[720px] mb-8 md:mb-0">
              Ripple USD is now available on the following exchanges. Start
              transacting today.
            </h4>
          </div>
        </PageGrid>
        <PageGrid className="lg:py-16">
          <div className="col-span-full grid grid-cols-2 md:grid-cols-4 gap-6 place-items-center">
            {nowAvailable.map((logo) => (
              <Link
                href={logo.link}
                target="_blank"
                rel="noopener noreferrer"
                key={logo.link}
                className="flex items-center justify-center p-4 w-full h-full"
              >
                <Image
                  width={logo.width}
                  height={logo.height}
                  src={logo.image}
                  alt={logo.alt}
                  className="object-contain hover:opacity-40 transition duration-150 ease-in-out p-1 max-h-20"
                />
              </Link>
            ))}
          </div>
        </PageGrid>
        <PageGrid>
          <p className="body4 text-center block text-gray-90 mt-10 col-span-full">
            **The list above is for educational purposes; Ripple does not
            endorse or recommend any of the above exchanges.
          </p>
        </PageGrid>
      </section>

      <div className="bg-gray-2.5 text-gray-90">
        <ValuePropsSection
          title="Why use Ripple USD?"
          valueProps={valueProps}
          description="Stability can’t exist without accountability. Ripple USD is built with regulatory compliance as a defining feature, bringing with it an opportunity for developers, payment providers, exchanges and others to benefit from the stability and transparency Ripple is uniquely able to provide with the first enterprise-ready stablecoin. With over a decade of experience at the intersection of crypto and finance, and a proven track record working with regulators and policymakers across the globe, Ripple is accelerating the digital asset economy with RLUSD."
          image={
            <div className="relative w-full lg:!absolute lg:w-double height-auto">
              <Image
                width={1366}
                height={768}
                src="/dashboard-ui/stablecoin.png"
                alt="Ripple USD (RLUSD) Stablecoin"
                className="w-full h-full object-cover box-shadow-3 rounded-lg"
              />
            </div>
          }
          buttonLink="/reports/rlusd-whitepaper.pdf"
          buttonCTA="Download the Whitepaper"
        />
      </div>
      <Element name="developers">
        <section className="py-12 md:py-16 bg-gray-0 text-gray-90">
          <PageGrid className="p-4">
            <div className="col-span-full lg:col-start-3 lg:col-span-8 text-center">
              <h3 className="headline4 mb-4">Dive into the Docs</h3>
              <p className="body3 lg:body2 mb-10">
                Access the documentation and Github repository to learn more
                about why Ripple created RLUSD and how to integrate it into your
                XRPL or Ethereum dApp.
              </p>
              <ul className="flex gap-10 justify-center">
                <li>
                  <Link
                    href="https://docs.ripple.com/stablecoin/"
                    target="_blank"
                    className="flex content-center  align-center"
                  >
                    Read the Docs
                    <LinkOutIcon className="ml-1" stroke="#6DC3FF" />
                  </Link>
                </li>
                <li>
                  <Link
                    className="flex content-center  align-center"
                    target="_blank"
                    href="https://github.com/ripple/RLUSD-Implementation"
                  >
                    Access the Repo
                    <LinkOutIcon className="ml-1" stroke="#6DC3FF" />
                  </Link>
                </li>
              </ul>
            </div>
          </PageGrid>
        </section>
      </Element>

      <CTACard {...cardData} />

      <SelectBlogPostsCarousel
        header="Read the latest on Stablecoins"
        data={carouselData}
        textColor="text-gray-90"
        secondaryTextColor="text-gray-50"
        darkMode={false}
        wrapperClass="py-10 md:py-24 bg-gray-2.5"
      />
      <section
        className="bg-gray-5 p-4 py-12 md:py-10 lg:py-24"
        id="stablecoin-form"
      >
        <div className="text-start grid xl:max-w-layout-xl lg:mx-auto lg:max-w-layout-lg">
          <div className="col-span-full">
            <PardotForm
              style={{ minWidth: "100%" }}
              src="https://go.ripple.com/l/105572/2025-01-20/qtx8km"
            />
          </div>
        </div>
      </section>
    </div>
  </StablecoinLayout>
)

export default StablecoinPage
